import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { MessageCircle, X, Send, Paperclip, FileText, Loader2 } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { ring, soundEnabled } from "../lib/chatSounds";
import { useIsMobile, mobileNavHidden } from "../hooks/useIsMobile";
import { isStaffOrRole } from "../lib/access";
import {
  fetchStatus,
  startConversation,
  getConversation,
  pollMessages,
  sendMessage,
  signalTyping,
  getSession,
  claimOutreach,
  clearSession,
  downloadAttachmentAsBlob,
  staffList,
} from "../lib/chatClient";

const POLL_MS = 3000;
const HIDE_ON_PREFIXES = ["/admin", "/checkout", "/tickets", "/proof/", "/invoice/", "/marketing"];
const STAFF_POLL_MS = 10_000;

function shouldHide(pathname) {
  return HIDE_ON_PREFIXES.some((p) => pathname === p || pathname.startsWith(p + "/") || pathname === p);
}

// Small floating launcher for logged-in staff. Renders on every page
// EXCEPT /admin/chat (where the full console lives) so the on-duty team
// can see incoming chats without leaving the shop / marketing site /
// wherever they are. Clicking jumps to /admin/chat.
function StaffChatLauncher() {
  const navigate = useNavigate();
  const location = useLocation();
  const [unread, setUnread] = useState(0);
  const [open, setOpen]     = useState(0);
  const seenRef = useRef(null); // conv_ids already known → ring only for brand-new requests

  useEffect(() => {
    let dead = false;
    const tick = async () => {
      try {
        const data = await staffList("open");
        if (dead) return;
        setUnread(data.unread || 0);
        setOpen(data.total_open || 0);
        const ids = new Set((data.rows || []).map((r) => r.conv_id));
        if (seenRef.current) {
          const fresh = (data.rows || []).some((r) => !seenRef.current.has(r.conv_id) && r.last_sender === "customer");
          if (fresh && soundEnabled()) ring();
        }
        seenRef.current = ids;
      } catch { /* silent */ }
    };
    tick();
    const id = setInterval(tick, STAFF_POLL_MS);
    return () => { dead = true; clearInterval(id); };
  }, []);

  if (shouldHide(location.pathname)) return null;
  return (
    <button
      type="button"
      onClick={() => navigate("/admin/chat")}
      className="fixed bottom-5 right-5 z-40 flex items-center gap-2 bg-[#333F48] hover:bg-[#1F2830] text-white font-bold pl-3 pr-4 py-3 rounded-full shadow-xl transition-transform hover:scale-105"
      data-testid="staff-chat-launcher-btn"
      aria-label={`Open live chat console (${unread} unread)`}
      title={`${open} open · ${unread} unread`}
    >
      <MessageCircle className="w-5 h-5" />
      <span className="hidden sm:inline text-sm">Live Chat</span>
      {unread > 0 && (
        <span
          className="ml-1 min-w-[20px] h-5 px-1.5 rounded-full bg-[#DD6B33] text-white text-[11px] font-black flex items-center justify-center animate-pulse"
          data-testid="staff-chat-launcher-unread"
        >
          {unread}
        </span>
      )}
    </button>
  );
}

function TimeStamp({ iso }) {
  const label = useMemo(() => {
    try {
      const d = new Date(iso);
      return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    } catch { return ""; }
  }, [iso]);
  return <span className="text-[10px] font-mono text-slate-400 ml-1.5">{label}</span>;
}

function AttachmentPill({ att }) {
  const [loading, setLoading] = useState(false);
  const open = async () => {
    setLoading(true);
    try {
      const blob = await downloadAttachmentAsBlob(att.attachment_id);
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } finally {
      setLoading(false);
    }
  };
  return (
    <button
      type="button"
      onClick={open}
      disabled={loading}
      className="mt-1 inline-flex items-center gap-1.5 px-2 py-1 rounded-sm border border-slate-300 bg-white/70 hover:bg-white text-[11px] font-mono text-slate-700 max-w-full"
      data-testid={`chat-attachment-${att.attachment_id}`}
    >
      {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileText className="w-3 h-3" />}
      <span className="truncate max-w-[160px]">{att.original_filename}</span>
    </button>
  );
}

function MessageBubble({ msg, mySide }) {
  const isMe = msg.sender_type === mySide;
  return (
    <div className={`flex ${isMe ? "justify-end" : "justify-start"}`} data-testid={`chat-msg-${msg.message_id}`}>
      <div
        className={`max-w-[80%] rounded-sm px-3 py-2 text-sm leading-relaxed break-words ${
          isMe
            ? "bg-[#333F48] text-white"
            : "bg-slate-100 text-slate-900 border border-slate-200"
        }`}
      >
        {!isMe && (
          <div className="text-[10px] font-mono uppercase tracking-wider text-[#DD6B33] mb-0.5">
            {msg.sender_name || (msg.sender_type === "staff" ? "Kolt team" : "Guest")}
          </div>
        )}
        {msg.body && <div className="whitespace-pre-wrap">{msg.body}</div>}
        {(msg.attachments || []).map((a) => (
          <AttachmentPill key={a.attachment_id} att={a} />
        ))}
        <div className={`mt-0.5 text-right ${isMe ? "opacity-75" : ""}`}>
          <TimeStamp iso={msg.created_at} />
        </div>
      </div>
    </div>
  );
}

function IntroForm({ onSubmit, defaultName, defaultEmail, submitting, staffOnline }) {
  const [name, setName]   = useState(defaultName || "");
  const [email, setEmail] = useState(defaultEmail || "");
  const [msg, setMsg]     = useState("");
  return (
    <form
      onSubmit={(e) => { e.preventDefault(); if (submitting) return; onSubmit({ name, email, msg }); }}
      className="p-4 space-y-3"
      data-testid="chat-intro-form"
    >
      <div className="text-sm text-slate-700 leading-relaxed">
        Hey! A Kolt teammate is here — tell us what you need and we&apos;ll reply in real time. We also email you a copy so you can pick it up from your inbox.
      </div>
      <div>
        <label className="block text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-1">Your name</label>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          maxLength={120}
          className="w-full border-2 border-slate-300 focus:border-[#333F48] rounded-sm px-2.5 py-1.5 text-sm outline-none"
          data-testid="chat-intro-name"
        />
      </div>
      <div>
        <label className="block text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-1">Email</label>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          className="w-full border-2 border-slate-300 focus:border-[#333F48] rounded-sm px-2.5 py-1.5 text-sm outline-none"
          data-testid="chat-intro-email"
        />
      </div>
      <div>
        <label className="block text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-1">How can we help?</label>
        <textarea
          value={msg}
          onChange={(e) => setMsg(e.target.value)}
          required
          minLength={2}
          maxLength={4000}
          rows={3}
          className="w-full border-2 border-slate-300 focus:border-[#333F48] rounded-sm px-2.5 py-1.5 text-sm outline-none resize-none"
          data-testid="chat-intro-message"
        />
      </div>
      <button
        type="submit"
        disabled={submitting}
        className="w-full flex items-center justify-center gap-2 bg-[#DD6B33] hover:bg-[#c95a25] disabled:opacity-60 disabled:cursor-not-allowed text-white font-bold py-2 rounded-sm text-sm"
        data-testid="chat-intro-submit"
      >
        {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Starting…</> : <>Start chat <Send className="w-4 h-4" /></>}
      </button>
    </form>
  );
}

export default function ChatWidget() {
  const { user } = useAuth();
  const staffUser = useMemo(() => isStaffOrRole(user, ["admin", "designer", "accountant"]), [user]);
  // Staff branch — small launcher badge everywhere so the on-duty team
  // can always see incoming chats and jump to the console. No customer
  // bubble.
  if (staffUser) return <StaffChatLauncher />;
  return <CustomerChatWidget user={user} />;
}

function CustomerChatWidget({ user }) {
  const location = useLocation();
  const hidden = shouldHide(location.pathname);
  const isMobile = useIsMobile();

  const [open, setOpen]         = useState(false);
  const [staffOnline, setStaff] = useState(false);
  const [conv, setConv]         = useState(null);
  const [messages, setMessages] = useState([]);
  const [starting, setStarting] = useState(false);
  const [sending, setSending]   = useState(false);
  const [draft, setDraft]       = useState("");
  const [files, setFiles]       = useState([]);
  const [error, setError]       = useState("");
  const [unread, setUnread]     = useState(0);
  const listRef  = useRef(null);
  const fileRef  = useRef(null);
  const pollRef  = useRef(null);
  const lastAtRef = useRef(null);
  const lastTypingRef = useRef(0);   // debounce: last time we pinged /typing

  const notifyTyping = useCallback(() => {
    if (!conv?.conv_id) return;
    const now = Date.now();
    if (now - lastTypingRef.current < 4000) return;  // once per 4s
    lastTypingRef.current = now;
    signalTyping(conv.conv_id);
  }, [conv?.conv_id]);

  // 1. On mount: check staff-online + resume any existing session.
  useEffect(() => {
    let dead = false;
    (async () => {
      try {
        const s = await fetchStatus();
        if (!dead) setStaff(!!s.online);
      } catch { /* silent */ }
      const sess = getSession();
      if (sess?.conv_id) {
        try {
          const data = await getConversation(sess.conv_id);
          if (dead) return;
          setConv(data.conversation);
          setMessages(data.messages);
          const last = data.messages[data.messages.length - 1];
          lastAtRef.current = last?.created_at || null;
        } catch {
          clearSession();
        }
      }
    })();
    return () => { dead = true; };
  }, []);

  // 1b. No conversation yet → every 8 s ask whether a staff member has
  //     opened a chat aimed at this visitor (Live Visitors → "Offer help").
  //     On a hit the panel pops open with their message already inside.
  useEffect(() => {
    if (conv?.conv_id || hidden) return;
    let dead = false;
    const tick = async () => {
      try {
        const data = await claimOutreach();
        if (dead || !data) return;
        setConv(data.conversation);
        setMessages(data.messages || []);
        const last = data.messages?.[data.messages.length - 1];
        lastAtRef.current = last?.created_at || null;
        setStaff(true);
        setOpen(true);
        setUnread(0);
      } catch { /* silent */ }
    };
    tick();
    const id = setInterval(tick, 8_000);
    return () => { dead = true; clearInterval(id); };
  }, [conv?.conv_id, hidden]);

  // 2. Poll while open + we have a conversation.
  useEffect(() => {
    if (!open || !conv?.conv_id) return;
    const tick = async () => {
      try {
        const data = await pollMessages(conv.conv_id, lastAtRef.current);
        if (data.messages?.length) {
          setMessages((prev) => {
            const seen = new Set(prev.map((m) => m.message_id));
            const merged = [...prev];
            for (const m of data.messages) if (!seen.has(m.message_id)) merged.push(m);
            return merged;
          });
          lastAtRef.current = data.messages[data.messages.length - 1].created_at;
        }
      } catch { /* silent — retry next tick */ }
    };
    pollRef.current = setInterval(tick, POLL_MS);
    return () => clearInterval(pollRef.current);
  }, [open, conv?.conv_id]);

  // 3. If widget is CLOSED, still poll every 15s just to update the unread badge.
  useEffect(() => {
    if (open || !conv?.conv_id) return;
    const id = setInterval(async () => {
      try {
        const data = await pollMessages(conv.conv_id, lastAtRef.current);
        if (data.messages?.length) {
          const newStaff = data.messages.filter((m) => m.sender_type === "staff");
          if (newStaff.length) setUnread((u) => u + newStaff.length);
          setMessages((prev) => {
            const seen = new Set(prev.map((m) => m.message_id));
            return [...prev, ...data.messages.filter((m) => !seen.has(m.message_id))];
          });
          lastAtRef.current = data.messages[data.messages.length - 1].created_at;
        }
      } catch { /* silent */ }
    }, 15_000);
    return () => clearInterval(id);
  }, [open, conv?.conv_id]);

  // 4. Autoscroll to newest message when messages update + panel open.
  useEffect(() => {
    if (!open || !listRef.current) return;
    listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, open]);

  const openPanel = useCallback(() => {
    setOpen(true);
    setUnread(0);
  }, []);

  // Phone bottom-nav "Chat" tab dispatches this event.
  useEffect(() => {
    window.addEventListener("kolt:open-chat", openPanel);
    return () => window.removeEventListener("kolt:open-chat", openPanel);
  }, [openPanel]);

  const handleStart = async ({ name, email, msg }) => {
    setError("");
    setStarting(true);
    try {
      const data = await startConversation({
        guest_name:    name,
        guest_email:   email,
        first_message: msg,
        page_url:      location.pathname + location.search,
      });
      setConv(data.conversation);
      setMessages(data.messages);
      setStaff(!!data.staff_online);
      const last = data.messages[data.messages.length - 1];
      lastAtRef.current = last?.created_at || null;
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not start chat");
    } finally {
      setStarting(false);
    }
  };

  const handleSend = async () => {
    if (!conv?.conv_id) return;
    if (!draft.trim() && !files.length) return;
    setSending(true);
    setError("");
    try {
      const m = await sendMessage(conv.conv_id, { body: draft, files });
      setMessages((prev) => [...prev, m]);
      lastAtRef.current = m.created_at;
      setDraft("");
      setFiles([]);
    } catch (e) {
      setError(e?.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  const handleReset = () => {
    clearSession();
    setConv(null);
    setMessages([]);
    setFiles([]);
    setDraft("");
    lastAtRef.current = null;
  };

  if (hidden) return null;

  // On phones the bottom-nav "Chat" tab replaces the floating bubble.
  const bubbleReplacedByNav = isMobile && !mobileNavHidden(location.pathname);
  const showBubble = !open && !bubbleReplacedByNav;

  return (
    <>
      {/* Floating launcher — on phones the bottom-nav "Chat" tab replaces it */}
      {showBubble && (
        <button
          type="button"
          onClick={openPanel}
          className="fixed bottom-28 right-5 z-40 flex items-center gap-2.5 bg-[#DD6B33] hover:bg-[#c95a25] text-white font-bold pl-3.5 pr-5 py-3 rounded-full shadow-xl transition-transform hover:scale-105"
          aria-label="Open live chat with a real person"
          data-testid="chat-launcher-btn"
        >
          <span className="relative flex h-2.5 w-2.5" aria-hidden>
            <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-300 opacity-75 animate-ping" />
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-400" />
          </span>
          <MessageCircle className="w-5 h-5" />
          <span className="hidden sm:flex flex-col leading-tight text-left">
            <span className="text-[10px] font-mono tracking-[0.2em] uppercase opacity-90">Human live chat</span>
            <span className="text-sm">Ask us anything</span>
          </span>
          {unread > 0 && (
            <span className="ml-1 min-w-[20px] h-5 px-1.5 rounded-full bg-white text-[#DD6B33] text-[11px] font-black flex items-center justify-center" data-testid="chat-unread-badge">
              {unread}
            </span>
          )}
        </button>
      )}

      {/* Panel */}
      {open && (
        <div
          className={`fixed z-[60] bg-white border-2 border-slate-900 shadow-2xl flex flex-col overflow-hidden ${
            isMobile ? "inset-0 rounded-none" : "bottom-5 right-5 w-[min(94vw,380px)] h-[min(80vh,600px)] rounded-sm"
          }`}
          data-testid="chat-panel"
        >
          {/* Header */}
          <div className="bg-[#1F2830] text-white px-4 py-3 flex items-center justify-between">
            <div className="min-w-0">
              <div className="font-display font-black text-sm leading-tight">Kolt Live Chat</div>
              <div className="text-[10px] font-mono uppercase tracking-widest flex items-center gap-1.5 mt-0.5">
                <span className="inline-block w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                Online now
              </div>
            </div>
            <div className="flex items-center gap-1">
              {conv && (
                <button
                  type="button"
                  onClick={handleReset}
                  className="text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-sm border border-white/30 hover:bg-white/10"
                  data-testid="chat-new-btn"
                >
                  New
                </button>
              )}
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Close chat"
                className="p-1 rounded-sm hover:bg-white/10"
                data-testid="chat-close-btn"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Body */}
          {!conv ? (
            <IntroForm
              onSubmit={handleStart}
              defaultName={user?.full_name || user?.name || ""}
              defaultEmail={user?.email || ""}
              submitting={starting}
              staffOnline={staffOnline}
            />
          ) : (
            <>
              <div
                ref={listRef}
                className="flex-1 overflow-y-auto p-3 space-y-2 bg-slate-50"
                data-testid="chat-message-list"
              >
                {messages.map((m) => (
                  <MessageBubble key={m.message_id} msg={m} mySide="customer" />
                ))}
                {conv.ticket_id && (
                  <div className="text-[11px] font-mono text-slate-500 border-l-2 border-emerald-400 pl-2 py-1">
                    A copy of this chat was sent to <strong>{conv.guest_email}</strong> (ref <strong>#{conv.ticket_id?.slice(-6)}</strong>) so you can also reply by email.
                  </div>
                )}
              </div>

              {/* Attached-files preview */}
              {files.length > 0 && (
                <div className="px-3 py-1.5 border-t border-slate-200 bg-white flex flex-wrap gap-1.5">
                  {files.map((f, i) => (
                    <div key={i} className="inline-flex items-center gap-1.5 px-2 py-0.5 border border-slate-300 rounded-sm text-[11px] font-mono text-slate-700">
                      <FileText className="w-3 h-3" />
                      <span className="max-w-[140px] truncate">{f.name}</span>
                      <button
                        type="button"
                        onClick={() => setFiles((arr) => arr.filter((_, j) => j !== i))}
                        aria-label={`Remove ${f.name}`}
                        className="text-slate-400 hover:text-slate-700"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Composer */}
              <div className="p-2.5 border-t border-slate-200 bg-white">
                {error && <div className="text-[11px] text-red-600 mb-1.5" data-testid="chat-error">{error}</div>}
                <div className="flex items-end gap-1.5">
                  <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    aria-label="Attach files"
                    className="p-2 rounded-sm hover:bg-slate-100 text-slate-600"
                    data-testid="chat-attach-btn"
                  >
                    <Paperclip className="w-4 h-4" />
                  </button>
                  <input
                    ref={fileRef}
                    type="file"
                    multiple
                    hidden
                    onChange={(e) => {
                      const list = Array.from(e.target.files || []).slice(0, 10);
                      setFiles((prev) => [...prev, ...list].slice(0, 10));
                      e.target.value = "";
                    }}
                    data-testid="chat-file-input"
                  />
                  <textarea
                    value={draft}
                    onChange={(e) => { setDraft(e.target.value); notifyTyping(); }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        handleSend();
                      }
                    }}
                    rows={1}
                    maxLength={4000}
                    placeholder="Type a message…"
                    className="flex-1 border-2 border-slate-300 focus:border-[#333F48] rounded-sm px-2.5 py-1.5 text-sm outline-none resize-none max-h-24"
                    data-testid="chat-draft-input"
                  />
                  <button
                    type="button"
                    onClick={handleSend}
                    disabled={sending || (!draft.trim() && !files.length)}
                    className="p-2 rounded-sm bg-[#DD6B33] hover:bg-[#c95a25] disabled:opacity-50 disabled:cursor-not-allowed text-white"
                    aria-label="Send"
                    data-testid="chat-send-btn"
                  >
                    {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
}
